import { Injectable, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { authActions } from '@zerops/zef/auth';
import { zefGithubLoginSuccess } from '@zerops/zef/github';
import { zefGitlabLoginSuccess } from '@zerops/zef/gitlab';
import { firstAvailable } from '@zerops/zef/core';
import { zefGo } from '@zerops/zef/ngrx-router';
// Already imported into core by zcp-pool-claim-base.effect for the same reason.
import { DEFAULT_APP_ROUTE } from '@zerops/zerops/app';
import { ZefSnackService } from '@zerops/zef/snack';
import { canViewFinances } from '@zerops/models/client-user';
import { PromoCodeRedemption } from '@zerops/models/promo-code';
import { concat, EMPTY, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  exhaustMap,
  filter,
  map,
  pairwise,
  switchMap,
  take,
  tap,
  timeout,
  withLatestFrom
} from 'rxjs/operators';
import { confirmPaymentSuccess } from '../billing-base';
import { UserEntity, storeUserDataSuccess } from '../user-base';
import { TransactionCreditEntity } from '../transaction-credit-base';
import {
  hasPendingZcpPoolClaim,
  PromoReveal,
  ZcpOnboardWizardService
} from '../zcp-pool-claim-base';
import { readCarriedDeviceLoginRequest } from '../device-login-base/device-login.carry';
import { readCarriedAuthorizeAppRequest } from '../z3-handover-base/z3-handover.carry';
import { selectSubmittedPromoCode } from './promo-code-base.selector';
import {
  clearSubmittedPromoCode,
  promoCodeArrived,
  promoCodeFlowResolved
} from './promo-code-base.action';
import {
  clearPendingPromoCode,
  drainPromoCodeFailureNote,
  hasParkedSignUpPromoCode,
  PendingPromoCode,
  readPendingPromoCode
} from './promo-code-base.util';
import {
  loadPromoCodeRedemptions,
  loadPromoCodeRedemptionsFail,
  loadPromoCodeRedemptionsSuccess,
  PromoCodeBaseActionUnion,
  redeemPromoCode,
  redeemPromoCodeFail,
  redeemPromoCodeSuccess
} from './promo-code-base.action';
import { PromoCodeBaseApi } from './promo-code-base.api';

// Long enough for conference wi-fi, short enough that the answer is still about
// the screen the user is looking at.
const FLOW_LOOKUP_TIMEOUT = 8000;

// The client user normally replays from the store instantly; this only stops a
// session that will never have one from waiting forever.
const CLIENT_USER_TIMEOUT = 10000;

@Injectable()
export class PromoCodeBaseEffect {
  // # Deps
  #actions$ = inject<Actions<PromoCodeBaseActionUnion>>(Actions);
  #api = inject(PromoCodeBaseApi);
  #userEntity = inject(UserEntity);
  #store = inject(Store);
  #snack = inject(ZefSnackService);
  #wizard = inject(ZcpOnboardWizardService);
  #transactionCreditEntity = inject(TransactionCreditEntity);

  // # Streams
  #activeClientId$ = this.#userEntity.activeClientId$.pipe(filter((d) => !!d));

  /**
   * Fully authenticated and org-resolved. Same gates the recipe-handoff and
   * ZCP-claim drains use: `refresh !== true` skips the org-switch reload,
   * a missing clientUserId skips a zero-account login.
   */
  #wizardState$ = toObservable(this.#wizard.state);
  // What we handed the wizard, held until its promo step actually shows it.
  #handedOverReveal: PromoReveal | undefined;
  // The code an arrival already routed, so the post-login path does not repeat
  // it. Read exactly once — see `#wasJustRoutedOnArrival`.
  #routedOnArrival: string | undefined;

  #onFullyAuthed$ = this.#actions$.pipe(
    ofType(storeUserDataSuccess),
    filter(({ refresh, clientUserId }) => refresh !== true && !!clientUserId)
  );


  // # Effects
  // The history GET allows every role and gates on the flag instead, same as
  // the rest of the billing reads.
  onActiveClientIdLoadRedemptions$ = createEffect(() => this.#activeClientId$.pipe(
    withLatestFrom(this.#userEntity.activeClientUser$),
    filter(([ _, clientUser ]) => canViewFinances(clientUser)),
    map(() => loadPromoCodeRedemptions())
  ));

  onLoadRedemptions$ = createEffect(() => this.#actions$.pipe(
    ofType(loadPromoCodeRedemptions),
    switchMap((action) => this.#userEntity.activeClientId$.pipe(
      firstAvailable(),
      switchMap((clientId) => this.#api
        .redemptions$(clientId)
        .pipe(
          map((res) => loadPromoCodeRedemptionsSuccess(res, action)),
          catchError((err) => of(loadPromoCodeRedemptionsFail(err, action)))
        )
      )
    ))
  ));

  // exhaustMap, not switchMap: redeeming is irreversible, so a second dispatch
  // must not cancel a request whose redemption the server may already have
  // performed — that would drop the response and lose the reveal.
  onRedeem$ = createEffect(() => this.#actions$.pipe(
    ofType(redeemPromoCode),
    exhaustMap((action) => this.#userEntity.activeClientId$.pipe(
      firstAvailable(),
      switchMap((clientId) => this.#api
        .redeem$(clientId, action.data)
        .pipe(
          map((res) => redeemPromoCodeSuccess(res, action)),
          catchError((err) => of(redeemPromoCodeFail(err, action)))
        )
      )
    ))
  ));

  // The credit itself arrives over the client-status websocket; only the
  // history list needs pulling again.
  onRedeemSuccessReloadRedemptions$ = createEffect(() => this.#actions$.pipe(
    ofType(redeemPromoCodeSuccess),
    map(() => loadPromoCodeRedemptions())
  ));

  /**
   * A code applied during a top-up is granted by Stripe's webhook, seconds after
   * the dialog closes — there is no response for us to react to. The redemption
   * history is a plain REST list with no websocket entity behind it, so it
   * cannot self-update the way the credit balance does.
   *
   * The transaction-credit list IS websocket-backed and gains a PROMO row for
   * exactly the same event, so use it as the trigger. `pairwise` after
   * `distinctUntilChanged` means the first emission never fires this — only a
   * genuine increase does, so page load does not cause a second fetch.
   */
  onPromoCreditGrantedReloadRedemptions$ = createEffect(() => this.#transactionCreditEntity.list$().pipe(
    filter((list) => !!list),
    map((list) => list.filter((item) => item.source === 'PROMO').length),
    distinctUntilChanged(),
    pairwise(),
    filter(([ previous, current ]) => current > previous),
    withLatestFrom(this.#userEntity.activeClientUser$),
    // The websocket push reaches every member of the org, but the history
    // endpoint needs the flag — without this a finance-blind member watching
    // an owner redeem a code gets an unexplained 403 snack.
    filter(([ , clientUser ]) => canViewFinances(clientUser)),
    map(() => loadPromoCodeRedemptions())
  ));

  /**
   * A code dropped during an OAuth signup is explained on the far side of the
   * provider round-trip the retry required — the redirect leaves the app, so
   * the reason travels in a cookie and is announced here, once, when the user
   * is back and authenticated.
   */
  onFullyAuthedAnnouncePromoCodeFailure$ = createEffect(() => this.#onFullyAuthed$.pipe(
    map(() => drainPromoCodeFailureNote()),
    filter((note): note is string => !!note),
    tap((note) => this.#snack.warning$({ text: note }, { duration: 10000 }))
  ), { dispatch: false });

  /**
   * A signup that turned into a login never sent the code anywhere — the OAuth
   * `emailExists` conversion and an abandoned registration both land here. The
   * pending marker has to go, or the reveal below would announce a code as
   * saved when nothing was submitted, while the cookie simultaneously offers it
   * for redemption.
   */
  onLoginSuccessClearSubmittedPromoCode$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.loginSuccess, zefGithubLoginSuccess, zefGitlabLoginSuccess),
    map(() => clearSubmittedPromoCode())
  ));

  /**
   * A code still waiting in the cookie once the session is up means nobody
   * consumed it — the user logged in rather than signed up. Land them on
   * Finances, which drains it into the field and scrolls to it.
   *
   * This lands after every default post-auth redirect without any of them
   * needing to know: they all dispatch `zefGo(DEFAULT_APP_ROUTE)` in the same
   * breath as `storeUserData`, so this action does not exist yet when they do.
   * A pending ZCP claim owns navigation outright, so leave it alone and let the
   * code keep until the user reaches Finances themselves.
   */
  onFullyAuthedPendingPromoCodeRoute$ = createEffect(() => this.#actions$.pipe(
    ofType(storeUserDataSuccess),
    // An org switch is a hard reload of an existing session, not a new one.
    filter(({ refresh }) => refresh !== true),
    map((action) => ({ action, pending: readPendingPromoCode() })),
    filter(({ pending }) => !!pending),
    // A cold-load scan reaches BOTH entry points — `Authorized` lands before
    // `storeUserDataSuccess`, and the cookie is not drained until Finances
    // loads, so this one would otherwise repeat the whole thing including a
    // second flow lookup. The arrival owns that code; this covers the login
    // case, where no arrival happened.
    //
    // Consumed BEFORE the `clientUserId` gate below, deliberately: a
    // zero-account session establishes one of these too, and a latch left
    // standing through it would go on to swallow the routing for the next real
    // login in this SPA lifetime.
    filter(({ pending }) => !this.#wasJustRoutedOnArrival(pending.code)),
    // Nowhere to route a session with no organisation — user-base sends it back
    // to login with `NO_ACTIVE_ACCOUNTS` on its own.
    filter(({ action }) => !!action.clientUserId),
    // A waiting device login or hand-over navigates to its consent screen, and the code keeps in its cookie.
    filter(() => !readCarriedDeviceLoginRequest() && !readCarriedAuthorizeAppRequest()),
    switchMap(({ pending }) => this.#routePendingCode$(pending.code))
  ));
  /**
   * A payment-mode code is only actually spent when the top-up completes, which
   * arrives over the websocket seconds after Stripe's webhook — long after the
   * dialog closed. `onPromoCreditGrantedReloadRedemptions$` refetches the
   * history for exactly that event; this turns the result into the amount on
   * screen, and drops the handover so Finances stops offering a spent code.
   */
  onPendingPromoCodeRedeemed$ = createEffect(() => this.#actions$.pipe(
    ofType(loadPromoCodeRedemptionsSuccess),
    map(({ data }) => {
      // The cookie while it is still waiting; the wizard's own copy once
      // Finances has drained it.
      const code = readPendingPromoCode()?.code ?? this.#wizard.promoReveal()?.code;
      return code
        ? data?.list?.find((item) => item.code === code)
        : undefined;
    }),
    filter((redemption): redemption is PromoCodeRedemption => !!redemption),
    tap((redemption) => {
      clearPendingPromoCode();
      // A no-op unless the reveal is the thing currently on screen.
      this.#wizard.updatePromoReveal({
        code: redemption.code,
        state: 'redeemed',
        amount: redemption.bonusCreditAmount
      });
    })
  ), { dispatch: false });

  /**
   * Stripe accepting the card is not the credit arriving: our backend hears it
   * from the webhook and relays it over the websocket seconds later, and the
   * dialog closes on the confirm. Without this the reveal underneath goes on
   * offering a top-up the user has just paid for, until
   * `onPendingPromoCodeRedeemed$` catches up.
   *
   * No deadline is promised in the copy and no timer backs this state, because
   * a webhook we do not control has no honest upper bound. The reveal's
   * Continue button is always there, so waiting is never a trap.
   */
  onPromoRevealPaymentConfirming$ = createEffect(() => this.#actions$.pipe(
    ofType(confirmPaymentSuccess),
    // A `success` action is not yet proof of payment: ngx-stripe resolves
    // rather than throws on a declined card, handing the outcome back in
    // `error` (see the TODO in top-up-dialog.container).
    filter(({ data }) => !data?.error
      && (data?.paymentIntent?.status === 'succeeded'
        || data?.paymentIntent?.status === 'processing')),
    map(() => this.#wizard.promoReveal()),
    // Only the two states a payment resolves. `redeemed` is already the end of
    // the line, and `awaiting-redeem` belongs to a code a payment does nothing
    // for, so a top-up alongside it must not claim to be settling it.
    filter((reveal): reveal is PromoReveal =>
      reveal?.state === 'needs-top-up' || reveal?.state === 'parked'),
    // A no-op unless the reveal is the thing currently on screen.
    tap((reveal) => this.#wizard.updatePromoReveal({
      code: reveal.code,
      state: 'confirming'
    }))
  ), { dispatch: false });

  /**
   * A reveal handed over during `claiming` is only shown once the claim
   * resolves and the wizard reaches its promo step. A claim that never resolves
   * times out instead, drops its layer and clears the reveal with it — so the
   * code would go unmentioned after a thirty-second wait, which is the worst of
   * both. Watch the layer go away without the promo step having been reached,
   * and say it the ordinary way instead.
   */
  onAbandonedPromoRevealFallBackToSnack$ = createEffect(() => this.#wizardState$.pipe(
    tap((state) => {
      if (!this.#handedOverReveal) { return; }

      if (state === 'promo') {
        // Shown, and the wizard owns it from here.
        this.#handedOverReveal = undefined;
        return;
      }

      if (state === 'idle' || state === 'done') {
        this.#snackReveal(this.#handedOverReveal);
        this.#handedOverReveal = undefined;
      }
    })
  ), { dispatch: false });

  /**
   * A ZCP-pool claim owns the screen outright, so the redirect above cannot
   * serve a still-pending code. Hand it to the wizard instead, which shows it
   * as its own step before the agent roster. The code stays in its cookie
   * either way, so Finances still fills it in later.
   */
  onPendingPromoCodeUnderZcpClaim$ = createEffect(() => this.#onFullyAuthed$.pipe(
    filter(() => hasPendingZcpPoolClaim()),
    map(() => readPendingPromoCode()),
    filter((pending): pending is PendingPromoCode => !!pending),
    switchMap((pending) => this.#resolvedClientUser$().pipe(
      switchMap((clientUser) => clientUser
        ? this.#appliesToTopUp$(pending.code)
        : of(false)),
      // Still in the cookie means nobody has consumed it, so this is never
      // `redeemed` — only which of the two waiting states it is. A code that
      // needs a payment says so and offers the top-up; one that does not points
      // at Finances, where the cookie is drained.
      tap((appliesToTopUp) => this.#wizard.setPromoReveal({
        code: pending.code,
        // `needs-top-up`, never `parked`: this code lives in our cookie, so it
        // applies only if the user attaches it to a payment. Saying it is
        // "saved" and lands on the first top-up would be the backend's promise,
        // not ours, and someone taking it at their word would pay without it.
        state: appliesToTopUp ? 'needs-top-up' : 'awaiting-redeem'
      }))
    ))
  ), { dispatch: false });

  /**
   * A code that went out with a signup has already been acted on server-side,
   * so there is nothing to redeem — only something to say. Registration returns
   * no redemption, so the amount is read back from the history.
   */
  onFullyAuthedRevealSubmittedPromoCode$ = createEffect(() => this.#onFullyAuthed$.pipe(
    withLatestFrom(this.#store.pipe(select(selectSubmittedPromoCode))),
    map(([ , submitted ]) => submitted),
    filter((submitted): submitted is PendingPromoCode => !!submitted),
    switchMap((submitted) => {
      // Captured now, synchronously: the claim drain clears its own cookie as
      // soon as the ZCP resolves, which can happen while the fetch below is
      // still in flight.
      const underZcpClaim = hasPendingZcpPoolClaim();

      return this.#userEntity.activeClientUser$.pipe(
        firstAvailable(),
        switchMap((clientUser) => {
          // The backend says outright whether the code is still waiting, so
          // only a code that was actually granted is worth looking up — and
          // only then is there an amount to look up.
          if (hasParkedSignUpPromoCode(clientUser?.client)) {
            return of<PromoCodeRedemption | undefined>(undefined);
          }

          return this.#api.redemptions$(clientUser.clientId).pipe(
            catchError(() => of({ list: [] as PromoCodeRedemption[], totalCount: 0 })),
            map(({ list }) => list?.find((item) => item.code === submitted.code))
          );
        }),
        tap((redemption) => this.#reveal(submitted, redemption, underZcpClaim))
      );
    }),
    map(() => clearSubmittedPromoCode())
  ));

  /**
   * A deep-linked code landing in a session that is already authenticated.
   * Same decision as the post-login case below, same code path.
   */
  onPromoCodeArrived$ = createEffect(() => this.#actions$.pipe(
    ofType(promoCodeArrived),
    tap(({ code }) => { this.#routedOnArrival = code; }),
    switchMap(({ code }) => this.#routePendingCode$(code))
  ));

  /**
   * Whether an arrival just routed this exact code.
   *
   * ONE-SHOT, and that is the whole point: logging out does not reload the app
   * (it is a `zefGo(['/login'])`), so a latch left standing would outlive its
   * session and silently swallow the routing for a later login — leaving that
   * user authenticated on the login page, because every default redirect
   * suppresses itself whenever a code is pending. Consuming it here means the
   * latch can only ever suppress the boot that immediately follows the arrival,
   * which is the only duplicate it exists to stop.
   */
  #wasJustRoutedOnArrival(code: string): boolean {
    const justRouted = code === this.#routedOnArrival;

    this.#routedOnArrival = undefined;

    return justRouted;
  }

  /**
   * The one place a pending code decides where the user goes. Both ways in —
   * arriving at /promo already authenticated, and finishing a login with a code
   * still in the cookie — come through here, so the two cannot drift.
   *
   * Navigates first and asks the endpoint second: nothing else navigates on
   * these paths, so a slow or failed lookup must never leave the user waiting
   * on it.
   */
  #routePendingCode$(code: string) {
    // A ZCP-pool claim owns the screen and the navigation outright — it goes to
    // the new project, or to the default route if it times out. Racing it would
    // put the user somewhere it did not choose. The code keeps in its cookie,
    // and onPendingPromoCodeUnderZcpClaim$ tells the wizard about it.
    if (hasPendingZcpPoolClaim()) { return EMPTY; }

    return this.#resolvedClientUser$().pipe(
      switchMap((clientUser) => {
        // Finances renders nothing for a member without the flag and consumes
        // the handover as it loads. Somewhere beats nowhere, and the code stays
        // in its cookie for an org where they can use it.
        if (!canViewFinances(clientUser)) {
          return of(zefGo(DEFAULT_APP_ROUTE));
        }

        return concat(
          of(zefGo([ '/', 'dashboard', 'finances', {} ])),
          // Recorded, not acted on. A payment dialog opening by itself moments
          // after a login reads as a demand rather than an offer, so the promo
          // section shows the code with a button to the top-up instead and the
          // user decides. That also means a code needing a payment is never
          // presented as a Redeem the backend is certain to reject.
          this.#appliesToTopUp$(code).pipe(
            map((appliesToTopUp) => promoCodeFlowResolved({ code, appliesToTopUp }))
          )
        );
      })
    );
  }

  /**
   * The active client user, or `undefined` if this session will never have one.
   * `firstAvailable()` is deliberately not used: it completes on the first
   * truthy value, so a client-less one would end the stream and emit nothing.
   * The bound covers a zero-account session, which never emits at all.
   */
  #resolvedClientUser$() {
    return this.#userEntity.activeClientUser$.pipe(
      filter((clientUser) => !!clientUser?.client),
      take(1),
      timeout({ first: CLIENT_USER_TIMEOUT, with: () => of(undefined) })
    );
  }

  /**
   * Whether the account has ever paid — read from a clientUser that actually
   * carries the field.
   *
   * `/api/user/info` projects the nested client down to `id`/`accountName`/
   * `avatar`, so for the whole of a fresh login `verifiedByTopUp` is undefined
   * there; the full record only arrives with the later client-user search.
   * Every other reader gets away with the gap because it renders through an
   * async pipe and re-renders when the real value lands — this is a one-shot
   * snapshot, so it has to wait for the field rather than for any client at all.
   *
   * Unknown resolves to verified, so the fallback is a Redeem button. Being
   * wrong that way answers with the backend's own message and a link to the
   * top-up; being wrong the other way tells someone who has already paid that
   * they have to pay again, which is what this bug did.
   */
  #isVerifiedClient$() {
    return this.#userEntity.activeClientUser$.pipe(
      filter((clientUser) => clientUser?.client?.verifiedByTopUp !== undefined),
      take(1),
      map((clientUser) => !!clientUser.client.verifiedByTopUp),
      timeout({ first: CLIENT_USER_TIMEOUT, with: () => of(true) })
    );
  }

  /**
   * Where a code can actually be applied right now, for this account.
   *
   * `VERIFIED` is the interesting one: it redeems on its own only once the
   * account has made a payment, so until then attaching it to a top-up is the
   * only thing that works immediately. It is also what the endpoint answers for
   * a code it does not recognise — deliberately, since the lookup must never
   * reveal whether a code exists — so this only ever picks a destination and
   * the backend still has the final say when the user acts.
   */
  #appliesToTopUp$(code: string) {
    return this.#api.flow$(code).pipe(
      // HttpClient has no timeout of its own, and a hung request must not leave
      // the promo section offering the wrong button indefinitely.
      timeout(FLOW_LOOKUP_TIMEOUT),
      switchMap(({ flow }) => {
        if (flow === 'TOP_UP') { return of(true); }
        if (flow === 'STANDALONE') { return of(false); }

        return this.#isVerifiedClient$().pipe(map((verified) => !verified));
      }),
      // Nothing is lost by guessing wrong: the code is already in the Finances
      // field, and a redeem that turns out to need a top-up answers with a link
      // across to it.
      catchError(() => of(false))
    );
  }


  /**
   * A standalone code was already redeemed by the signup call, so the amount is
   * the whole message. Where it goes depends on who owns the screen: under a
   * ZCP-pool claim the wizard shows it as its own step, which is the one moment
   * the user is actually looking. Everywhere else a snack is enough, because
   * they are already on a page that shows the credit.
   */
  #reveal(
    submitted: PendingPromoCode,
    redemption: PromoCodeRedemption | undefined,
    underZcpClaim: boolean
  ): void {
    // No redemption means the code was parked rather than granted — a
    // standalone code in a verified-clients-only category, or any payment-mode
    // one. The backend holds it and applies it to the first top-up, so there is
    // no amount yet; say it is waiting rather than saying nothing, which is how
    // a coin goes quiet on its owner. Also the safety net for a history fetch
    // that came back empty when it should not have.
    const reveal: PromoReveal = redemption
      ? { code: submitted.code, state: 'redeemed', amount: redemption.bonusCreditAmount }
      // `parked`: the backend is holding this one against the first payment,
      // whatever that payment turns out to be.
      : { code: submitted.code, state: 'parked' };

    if (underZcpClaim) {
      this.#handedOverReveal = reveal;
      this.#wizard.setPromoReveal(reveal);
      return;
    }

    this.#snackReveal(reveal);
  }

  #snackReveal(reveal: PromoReveal): void {
    if (reveal.state === 'redeemed') {
      this.#snack.success$({
        text: `Promo code ${reveal.code} redeemed`
          + ` — ${reveal.amount} promo credit added.`
      }, { duration: 8000 });
      return;
    }

    this.#snack.info$({
      text: reveal.state === 'parked'
        ? `Promo code ${reveal.code} is saved — it applies to your first top-up.`
        : reveal.state === 'needs-top-up'
          ? `Promo code ${reveal.code} is redeemed as part of a top-up.`
          : `Promo code ${reveal.code} is waiting — redeem it on the Finances page.`
    }, { duration: 8000 });
  }
}
